import { forwardRef, InputHTMLAttributes, ReactNode, useState } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  floatingLabel?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      floatingLabel = false,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = props.value !== undefined && props.value !== '';
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    if (floatingLabel && label) {
      return (
        <div className="relative">
          <div className="relative">
            {leftIcon && (
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                {leftIcon}
              </div>
            )}
            <input
              ref={ref}
              id={inputId}
              className={`
                peer w-full bg-white border rounded-xl
                text-gray-900 placeholder-transparent
                transition-all duration-200
                ${leftIcon ? 'pl-11' : 'px-4'}
                ${rightIcon ? 'pr-11' : 'px-4'}
                pt-6 pb-2
                ${error
                  ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  : 'border-gray-200 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 hover:border-gray-300'
                }
                ${className}
              `}
              placeholder={label}
              onFocus={(e) => {
                setIsFocused(true);
                props.onFocus?.(e);
              }}
              onBlur={(e) => {
                setIsFocused(false);
                props.onBlur?.(e);
              }}
              {...props}
            />
            <label
              htmlFor={inputId}
              className={`
                absolute left-4 transition-all duration-200 pointer-events-none
                ${leftIcon ? 'left-11' : 'left-4'}
                ${isFocused || hasValue
                  ? 'top-2 text-xs'
                  : 'top-1/2 -translate-y-1/2 text-base'
                }
                ${error ? 'text-red-500' : isFocused ? 'text-kenagri-600' : 'text-gray-500'}
              `}
            >
              {label}
            </label>
            {rightIcon && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                {rightIcon}
              </div>
            )}
          </div>
          {(error || hint) && (
            <p className={`mt-1.5 text-sm ${error ? 'text-red-500' : 'text-gray-500'}`}>
              {error || hint}
            </p>
          )}
        </div>
      );
    }

    return (
      <div>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={`
              w-full bg-white border rounded-xl
              text-gray-900 placeholder-gray-400
              transition-all duration-200
              py-3
              ${leftIcon ? 'pl-11' : 'px-4'}
              ${rightIcon ? 'pr-11' : 'px-4'}
              ${error
                ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                : 'border-gray-200 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 hover:border-gray-300'
              }
              ${className}
            `}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
              {rightIcon}
            </div>
          )}
        </div>
        {(error || hint) && (
          <p className={`mt-1.5 text-sm ${error ? 'text-red-500' : 'text-gray-500'}`}>
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
